import React, { useEffect, useRef, useState } from 'react';
import './PayerIdInput.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Inline editor for a practice payer's Stedi payer id (PracticePayer.payer_id, what a
// 270 is addressed to). Blur or Enter saves it with one PUT of {payer_id}; Escape puts
// the stored value back. Shared by the Eligibility check form and Practice Details ->
// Payers so the save, and the wording of its failure, live in one place. The server
// validates the value; this only trims it.
function PayerIdInput({ practiceId, payer, onSaved, onError, inputRef, className = '' }) {
  const stored = (payer && payer.payer_id) || '';
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false); // blur after Enter must not send a second PUT
  const skipBlurSave = useRef(false);

  useEffect(() => { setDraft(stored); }, [payer && payer.id, stored]);

  const save = async () => {
    if (!payer || savingRef.current) return;
    const value = draft.trim();
    if (value === stored) { setDraft(stored); return; }
    savingRef.current = true;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/practices/${practiceId}/payers/${payer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payer_id: value }),
      });
      let data = {};
      try { data = await response.json(); } catch (_) { data = {}; }
      if (!response.ok) {
        setDraft(stored);
        onError((data && data.error) || 'Failed to save payer ID');
        return;
      }
      onSaved({ ...payer, ...data, payer_id: data.payer_id != null ? data.payer_id : value });
    } catch (_) {
      setDraft(stored);
      onError('Network error. The payer ID was not saved.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur(); // the blur saves
    } else if (e.key === 'Escape') {
      skipBlurSave.current = true;
      setDraft(stored);
      e.currentTarget.blur();
    }
  };

  const handleBlur = () => {
    if (skipBlurSave.current) { skipBlurSave.current = false; return; }
    save();
  };

  if (!payer) return null;
  const classes = ['payer-id-input', saving ? 'is-saving' : '', !stored ? 'is-empty' : '', className]
    .filter(Boolean).join(' ');
  return (
    <input
      ref={inputRef}
      type="text"
      className={classes}
      aria-label="Payer ID"
      placeholder="Payer ID"
      title={stored ? 'Stedi payer ID. Edit and press Enter to save.' : 'No Stedi payer ID yet. Enter one and press Enter to save.'}
      value={draft}
      maxLength={50}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}

export default PayerIdInput;
